// ─── A carga do comprador (Mercado C5) ───────────────────────────────────────
//
// Tipos e DTOs do módulo Comprador. Mora em `shared/` porque tela e servidor
// leem o MESMO contrato: a lista de cargas, o resumo derivado e a carteira de
// produtores atravessam a fronteira inteiros, e duas definições divergiriam no
// primeiro campo novo.
//
// ⚠️ SEM drizzle aqui. Este arquivo é importado pelo client; `schema.ts` arrasta
// o ORM inteiro junto e estourou o bundle antes.
//
// ⚠️ NADA DERIVADO É EDITÁVEL — e isso é contrato de tipo, não convenção: custo,
// despesas, kg líquido, faltam, margem e saldo só aparecem em `ResumoCarga`
// (saída), nunca em `CargaPatch` ou `NovaCarga` (entrada). Um campo derivado que
// aceita escrita é como o total da venda passou a divergir dos itens.

import { somarDiasCivis } from "./data-fazenda.js";

// ─── Estados ─────────────────────────────────────────────────────────────────

/**
 * EM_FORMACAO → FECHADA → ENTREGUE. Um caminho só, e reabrir volta um passo.
 *
 * ⚠️ Eixo SEPARADO do dinheiro (`status_recebimento`, PR D). "Entregue e não
 * paga" é o estado mais comum de todos; um status único teria que enumerar o
 * produto cartesiano dos dois eixos, e é assim que se chega a estados que
 * ninguém sabe explicar.
 */
export const CARGA_STATUS = {
  EM_FORMACAO: "EM_FORMACAO",
  FECHADA: "FECHADA",
  ENTREGUE: "ENTREGUE",
} as const;
export type CargaStatus = keyof typeof CARGA_STATUS;

export const CARGA_STATUS_LABEL: Record<CargaStatus, string> = {
  EM_FORMACAO: "Em formação",
  FECHADA: "Fechada",
  ENTREGUE: "Entregue",
};

/** Só EM_FORMACAO aceita item. Depois de fechada, o documento já foi emitido. */
export function aceitaItem(status: string): boolean {
  return status === CARGA_STATUS.EM_FORMACAO;
}

/**
 * Despesa entra até FECHADA — o frete chega por WhatsApp depois de o caminhão
 * sair, e recusá-lo obrigaria a reabrir a carga (gerando evento de reabertura)
 * só para lançar um valor que não muda o documento do destino.
 */
export function aceitaDespesa(status: string): boolean {
  return status === CARGA_STATUS.EM_FORMACAO || status === CARGA_STATUS.FECHADA;
}

export const CARGA_DESPESA_TIPO = {
  FRETE: "FRETE",
  EMBALAGEM: "EMBALAGEM",
  CARREGAMENTO: "CARREGAMENTO",
  PEDAGIO: "PEDAGIO",
  OUTRA: "OUTRA",
} as const;
export type CargaDespesaTipo = keyof typeof CARGA_DESPESA_TIPO;

export const CARGA_DESPESA_TIPOS: CargaDespesaTipo[] = [
  "FRETE", "EMBALAGEM", "CARREGAMENTO", "PEDAGIO", "OUTRA",
];

export const CARGA_DESPESA_LABEL: Record<CargaDespesaTipo, string> = {
  FRETE: "Frete",
  EMBALAGEM: "Embalagem",
  CARREGAMENTO: "Carregamento",
  PEDAGIO: "Pedágio",
  OUTRA: "Outra",
};

/**
 * A natureza do item. Não é flag de completude: ROMANEIO é um documento que o
 * produtor emitiu no Rizoma, REGISTRO é o comprador anotando o que combinou com
 * quem não tem conta. As duas somam na mesma carga.
 */
export const ITEM_NATUREZA = { ROMANEIO: "ROMANEIO", REGISTRO: "REGISTRO" } as const;
export type ItemNatureza = keyof typeof ITEM_NATUREZA;

/**
 * O que uma linha de "A pagar" pode ser.
 *
 * As duas primeiras são as naturezas do ITEM da carga (o comprador devendo ao
 * produtor). `CARGA` é o TERCEIRO tipo, e existe só para o DISTRIBUIDOR com
 * conta (C8b): a dívida dele não é com um produtor, é a carga inteira com quem a
 * formou — a mesma tela, uma coluna "documento" a mais.
 */
export type NaturezaDivida = ItemNatureza | "CARGA";

/** Quem escreveu o vencimento (regra 6 — os dois lados escrevem, vale o último). */
export const VENCIMENTO_DEFINIDO_POR = {
  PRODUTOR: "PRODUTOR",
  COMPRADOR: "COMPRADOR",
  DESTINO: "DESTINO",
} as const;

// ─── O item de registro próprio ──────────────────────────────────────────────

/**
 * Uma linha do que o comprador anotou. `precoKg` é o COMBINADO com aquele
 * produtor — não a cotação da praça, que entra na tela só como referência.
 */
export interface ItemRegistrado {
  variedade: string;
  /** "1ª", "2ª"… NULL = a variedade não tem qualidade (Nanica). */
  qualidade: string | null;
  kg: number;
  precoKg: number;
}

/**
 * Onde o dinheiro cai. Em `organizations.dados_pagamento` (cadastro, digitado
 * uma vez) e copiado para `mercado_cargas.pagamento_snapshot` ao FECHAR: trocar
 * a chave PIX amanhã não pode reescrever o que o destino leu no documento de
 * ontem.
 *
 * Tudo opcional porque tudo é opcional na vida real: quem só usa PIX não tem
 * agência, e obrigar os cinco campos faria o comprador preencher "0000".
 */
export interface DadosPagamento {
  pix?: string | null;
  banco?: string | null;
  agencia?: string | null;
  conta?: string | null;
  titular?: string | null;
}

/** Padrão de prazo do registro próprio: 15 dias do carregamento. */
export const DIAS_VENCIMENTO_PADRAO_ITEM = 15;

/** Atalhos do seletor de vencimento do ITEM — contados do CARREGAMENTO. */
export const ATALHOS_VENCIMENTO_ITEM = [7, 15, 30] as const;

/** Atalhos do vencimento da VENDA — contados da ENTREGA, e prazos maiores. */
export const ATALHOS_VENCIMENTO_VENDA = [15, 30, 45] as const;

/**
 * 'CG-0001' — o próximo identificador da org.
 *
 * Puro e sobre a LISTA (não `MAX(...)` no SQL) para caber num teste: o formato é
 * contrato com o destino (ele lê "CG-0031" no PDF e no WhatsApp), e o zero à
 * esquerda mantém a ordem alfabética igual à numérica. Concorrência é resolvida
 * por fora, pelo UNIQUE (org, identificador) + retry — igual a `OF-`.
 */
export function proximoIdentificadorCarga(existentes: string[]): string {
  let maior = 0;
  for (const id of existentes) {
    const m = /^CG-(\d+)$/.exec(String(id ?? "").trim());
    if (!m) continue;
    const n = parseInt(m[1], 10);
    if (n > maior) maior = n;
  }
  const proximo = String(maior + 1);
  return `CG-${proximo.length >= 4 ? proximo : "0000".substring(proximo.length) + proximo}`;
}

/**
 * O vencimento que o registro próprio nasce com: carregamento + 15 dias.
 * Sem data de carregamento não há de onde contar, e `null` ("a definir") é
 * estado normal — inventar uma data a partir de hoje criaria um prazo que
 * ninguém combinou.
 */
export function vencimentoPadraoDoItem(dataCarregamento: string | null): string | null {
  if (!dataCarregamento) return null;
  return somarDiasCivis(dataCarregamento, DIAS_VENCIMENTO_PADRAO_ITEM);
}

// ─── Carteira de produtores ──────────────────────────────────────────────────

export interface ProdutorCarteiraDTO {
  id: string;
  nome: string;
  apelidos: string[];
  telefone: string | null;
  cidade: string | null;
  /** Tem conta no Rizoma (`org_produtor_id` preenchido). */
  noRizoma: boolean;
  /** Vínculo ATIVO com esta org compradora. */
  vinculado: boolean;
  // ⚠️ `romaneios`, `kgTotal`, `aPagar` e `ultimoEm` SAÍRAM (Produtores v2):
  // contavam só item dentro de carga. Os números da carteira são do painel,
  // `CarteiraDTO`, que lê a mesma união de Romaneios e a mesma dívida de A pagar.
  /**
   * O token `/p/` do último REGISTRO PRÓPRIO deste contato (C5b) — é o que o
   * botão "Convidar" manda.
   *
   * `null` para quem já está no Rizoma (o romaneio dele tem o `/v/` próprio) e
   * para quem nunca teve registro. Convidar sem link seria convidar para lugar
   * nenhum, e por isso o botão some.
   */
  ultimoItemToken: string | null;
}

// ─── Produtores — o painel da carteira (Produtores v2) ───────────────────────
//
// ⚠️ UM PAINEL, NÃO UM CADASTRO. "De quem eu compro, quanto, quanto devo a cada
// um e há quanto tempo não compro." O cadastro continua (`ProdutorCarteiraDTO`,
// acima) para os seletores e o autocomplete; o painel é outra pergunta, com
// outra forma.
//
// ⚠️ AS DUAS FONTES SÃO AS DAS OUTRAS TELAS, e só elas: o volume sai da MESMA
// união de Romaneios (`listarRomaneios` — as duas naturezas, com e sem carga,
// pela data do documento) e a dívida sai da MESMA função de A pagar
// (`linhasAPagar`). Um segundo leitor de romaneio foi o que deixou a Fazenda Boa
// Vista com "Romaneios 0 · 0 kg" tendo quatro romaneios em aberto.

export const PERIODOS_CARTEIRA = ["30d", "90d", "ano"] as const;
export type PeriodoCarteira = typeof PERIODOS_CARTEIRA[number];

export const SITUACOES_CARTEIRA = ["COM_ABERTO", "COM_VENCIDO", "SEM_COMPRA"] as const;
export type SituacaoCarteira = typeof SITUACOES_CARTEIRA[number];

export const ORDENS_CARTEIRA = ["aberto", "comprado", "ultima", "nome"] as const;
export type OrdemCarteira = typeof ORDENS_CARTEIRA[number];

export interface FiltroCarteira {
  periodo?: PeriodoCarteira;
  situacao?: SituacaoCarteira;
  origem?: "RIZOMA" | "REGISTRO_PROPRIO";
  /** Nome, apelido ou telefone. */
  q?: string;
  ordem?: OrdemCarteira;
  /**
   * Um produtor só — a faixa de resumo de Romaneios. É a MESMA função com um
   * recorte, e não uma segunda consulta de dívida.
   */
  contatoId?: string;
}

export interface LinhaCarteiraDTO {
  contatoId: string;
  nome: string;
  apelidos: string[];
  /** Fazenda (razão social) do romaneio mais recente; `null` sem romaneio. */
  fazenda: string | null;
  cidade: string | null;
  telefone: string | null;
  noRizoma: boolean;
  vinculado: boolean;
  /** O `/p/` do último registro próprio — é o que "Convidar" manda. */
  ultimoItemToken: string | null;
  /** Do PERÍODO escolhido. */
  periodo: {
    romaneios: number;
    /** kg pela base: conferido quando existe, senão emitido. */
    kg: number;
    valor: number;
    precoMedioKg: number | null;
    /** Nº de cargas DISTINTAS com romaneios deste produtor no período. */
    cargas: number;
    porClassificacao: { classificacao: string; kg: number }[];
  };
  /** SEM período: dívida antiga continua dívida. Soma dos saldos em A pagar. */
  emAberto: number;
  documentosEmAberto: number;
  vencido: number;
  proximoVencimento: string | null;
  /** SEM período: a data do documento mais recente. */
  ultimaCompraEm: string | null;
}

export interface CarteiraDTO {
  periodo: PeriodoCarteira;
  /** "30 dias", "90 dias", "2026" — o rótulo dos tiles e das colunas. */
  rotuloPeriodo: string;
  /**
   * Da carteira INTEIRA no período. Nenhum chip, origem ou busca mexe aqui: os
   * tiles respondem "como está minha carteira", e um tile que mudasse com a
   * busca responderia outra coisa a cada letra.
   */
  kpis: {
    comprando: { n: number; total: number; noRizoma: number; registroProprio: number };
    comprado: { kg: number; valor: number; precoMedioKg: number | null };
    emAberto: { valor: number; documentos: number; produtores: number };
    vencido: { valor: number; produtores: number };
  };
  /** Por situação, com período/origem/busca aplicados — os números dos chips. */
  contagens: { todos: number; COM_ABERTO: number; COM_VENCIDO: number; SEM_COMPRA: number };
  linhas: LinhaCarteiraDTO[];
  /** Da lista FILTRADA — o rodapé da tabela. */
  totais: { produtores: number; romaneios: number; kg: number; valor: number; emAberto: number };
}

/** O resultado de procurar um produtor por texto (autocomplete e duplicata). */
export interface ResolucaoProdutor {
  /** Casou telefone, ou nome idêntico depois de normalizar. */
  exato: ProdutorCarteiraDTO | null;
  /** Nomes/apelidos parecidos — é o que a tela pergunta antes de criar. */
  parecidos: ProdutorCarteiraDTO[];
}

export interface DestinoDTO {
  id: string;
  nome: string;
  cidade: string | null;
  telefone: string | null;
  kgCaixa: number | null;
  /** O distribuidor tem conta no Rizoma (`org_destino_id`). */
  noRizoma: boolean;
}

// ─── A carga ─────────────────────────────────────────────────────────────────

/**
 * Tudo que a tela mostra e NADA que ela escreve. Os oito primeiros campos são
 * derivados a cada leitura por `derivados.ts`; `valorVenda` é o único que troca
 * de natureza (derivado enquanto EM_FORMACAO, snapshot depois) e `vendaEstimada`
 * é o que diz qual dos dois a tela está lendo.
 */
export interface ResumoCarga {
  kgLiquido: number;
  custo: number;
  despesas: number;
  /** kg que faltam para o alvo. `null` sem alvo — não é zero. */
  faltam: number | null;
  /** `null` quando o perfil não é CORRETOR ou não há preço de venda. */
  valorVenda: number | null;
  margem: number | null;
  /** Fração (0,065 = 6,5%), não porcentagem — a tela formata. */
  margemPct: number | null;
  margemKg: number | null;
  /** (custo + despesas) / kg líquido. `null` com kg 0. */
  custoDesembarcadoKg: number | null;
  nProdutores: number;
  kgBruto: number | null;
  /** kgBruto − kgLiquido, quando há bruto. */
  diferencaBruto: number | null;
  /** true = `valorVenda`/`margem` vêm do preço × kg de agora, não do snapshot. */
  vendaEstimada: boolean;
}

/**
 * O resumo que a TELA DO CORRETOR lê — SEM dados de venda (decisão D2).
 *
 * ⚠️ Não é o `ResumoCarga` de `derivados.ts`, e a diferença é deliberada:
 * `derivados` continua sabendo calcular custo, margem e valor de venda (as
 * colunas seguem no banco, e o "A receber" do distribuidor continua usando), mas
 * NADA disso atravessa para estas telas nesta fase. Um tipo que carrega o campo
 * "só por enquanto zerado" é como o número volta a aparecer.
 *
 * ⚠️ `valorPagoProdutores` é o que ele DEVE — Σ das bases dos romaneios. Aparece
 * só na montagem da carga, nunca no documento nem no `/c/` (D6).
 */
export interface ResumoCargaDoCorretor {
  kgLiquido: number;
  valorPagoProdutores: number;
  despesas: number;
  /** kg que faltam para o alvo. `null` sem alvo — não é zero. */
  faltam: number | null;
  nProdutores: number;
  nRomaneios: number;
  kgBruto: number | null;
  /** kgBruto − kgLiquido, quando há bruto. */
  diferencaBruto: number | null;
  /** Fração (0,77 = 77% do caminhão), não porcentagem — a tela formata. */
  pctDoAlvo: number | null;
}

/** Uma faixa do bloco "Por classificação" — o que vai no documento. */
export interface LinhaPorClassificacaoDTO {
  chave: string;
  classificacao: string;
  kg: number;
  /** Fração do total da carga. */
  pct: number;
}

/** O que o corretor escolheu mostrar no documento desta carga (D5). */
export interface ConfigDocumentoDTO {
  mostrarProdutores: boolean;
  mostrarBruto: boolean;
  /** ISO do evento `CARGA_DOCUMENTO_ENVIADO` mais recente. `null` = não enviado. */
  enviadoEm: string | null;
}

export interface DespesaDTO {
  id: string;
  tipo: string;
  descricao: string | null;
  valor: number;
}

export interface ItemDaCargaDTO {
  id: string;
  natureza: ItemNatureza;
  produtorContatoId: string;
  produtorNome: string;
  /** true quando o contato tem conta no Rizoma — o chip verde. */
  noRizoma: boolean;
  /** "romaneio 042/2026" ou "bloco nº 2214" — subtítulo da linha. */
  origem: string;
  /** O nº do talão de papel (natureza REGISTRO, 073). `null` no romaneio. */
  numeroBloco: string | null;
  /** A data do documento do registro próprio (073). `null` no romaneio — ela é da venda. */
  dataDocumento: string | null;
  /** kg da conferência quando há, senão do romaneio/registro. */
  kg: number;
  /** O que este item CUSTA ao comprador (base do pagamento). */
  valor: number;
  /** valor / kg. `null` com kg 0. */
  basePorKg: number | null;
  /** Só natureza REGISTRO; natureza ROMANEIO devolve `null` (os itens são do romaneio). */
  itens: ItemRegistrado[] | null;
  vencimento: string | null;
  vencimentoDefinidoPor: string | null;
  conferidoEm: string | null;
  conferenciaObs: string | null;
  /** O comprador conferiu o romaneio (natureza ROMANEIO). */
  romaneioConferido: boolean;
  /** PR B: o link /p/ já foi gerado. */
  temLink: boolean;
  /** PR B: o item virou venda no Cacho do produtor. */
  adotadoEm: string | null;
  /** A `ref` do romaneio (`c:`/`r:`) — abre o painel de Romaneios a partir da carga. */
  ref: string;
  /** kg por chave de classificação — as colunas da tabela da montagem. */
  porClassificacao: Record<string, number>;
  /** A data do documento: a da venda no romaneio, a do carregamento no registro. */
  data: string | null;
  /** A_CONFERIR | CONFERIDO | AJUSTADO — `null` no registro próprio. */
  conferencia: string | null;
}

/**
 * Um card do quadro de cargas.
 *
 * ⚠️ SEM `precoVendaKg`, `vencimentoVenda` nem margem (D2): nesta fase a carga é
 * o controle do corretor — o que ele acompanha aqui é o que está montando, o que
 * já saiu e o que foi entregue. As colunas seguem no banco.
 */
export interface CargaLinhaDTO {
  id: string;
  identificador: string;
  status: CargaStatus;
  variedade: string | null;
  praca: string | null;
  dataCarregamento: string | null;
  alvoKg: number | null;
  /** O CLIENTE (`destino` no banco, "Cliente" em toda a superfície — D3). */
  clienteNome: string | null;
  clienteLocal: string | null;
  entregueEm: string | null;
  fechadaEm: string | null;
  /** Iniciais dos produtores da carga, para os avatares do card. */
  produtores: { iniciais: string; noRizoma: boolean }[];
  /** ISO do envio do documento ao cliente. `null` = não enviado. */
  documentoEnviadoEm: string | null;
  resumo: ResumoCargaDoCorretor;
}

export interface CargaDetalheDTO extends CargaLinhaDTO {
  destinoContatoId: string | null;
  placa: string | null;
  motoristaNome: string | null;
  motoristaTelefone: string | null;
  observacao: string | null;
  mostrarDespesas: boolean;
  /** PR B: o link /c/ já foi gerado (só depois de fechar). */
  temLink: boolean;
  /** O bloco "Por classificação" — o que vai no documento. */
  porClassificacao: LinhaPorClassificacaoDTO[];
  /** D5 — o que aparece no documento desta carga, e se já foi enviado. */
  documento: ConfigDocumentoDTO;
  itens: ItemDaCargaDTO[];
  despesas: DespesaDTO[];
  perfil: string | null;
}

// ─── O quadro (Cargas v2) ────────────────────────────────────────────────────

/**
 * Os quatro números do topo de Cargas.
 *
 * ⚠️ NENHUM em reais (D2). O que ele acompanha nesta tela é volume e estado: o
 * que falta para fechar o caminhão que está montando, o que já saiu e se o
 * cliente recebeu o documento — que é a pendência que costuma escapar.
 */
export interface KpisCargas {
  emFormacao: { n: number; faltamPorCarga: { identificador: string; faltam: number | null }[] };
  fechadas: { n: number; comDocumentoEnviado: number };
  entreguesNoPeriodo: { n: number; kg: number; nRomaneios: number };
  romaneiosSemCarga: { n: number; kg: number; produtores: string[] };
}

export interface QuadroCargasDTO {
  kpis: KpisCargas;
  /**
   * ⚠️ EM_FORMACAO e FECHADA aparecem SEMPRE, fora do período: o que está
   * andando não pode sumir da tela porque o mês virou. Só ENTREGUE respeita o
   * recorte — ali o período é a pergunta ("quanto saiu em setembro?").
   */
  colunas: {
    EM_FORMACAO: CargaLinhaDTO[];
    FECHADA: CargaLinhaDTO[];
    ENTREGUE: CargaLinhaDTO[];
  };
}

/** Um romaneio formalizado que ainda não está em carga nenhuma. */
export interface RomaneioDisponivelDTO {
  compartilhamentoId: string;
  produtorNome: string;
  /** "042/2026" ou "sem número". */
  numero: string;
  data: string;
  kg: number;
  valor: number;
  conferido: boolean;
  vencimento: string | null;
}

// ─── Entradas (o que a tela escreve) ─────────────────────────────────────────

/**
 * ⚠️ SEM `precoVendaKg` (D2). A rota ignora o campo se ele vier — a carga, nesta
 * fase, nasce sem dados de venda, e aceitar o preço aqui o faria aparecer no
 * documento de novo pela porta dos fundos.
 */
export interface NovaCarga {
  variedade?: string | null;
  praca?: string | null;
  dataCarregamento?: string | null;
  alvoKg?: number | null;
  destinoContatoId?: string | null;
  placa?: string | null;
  motoristaNome?: string | null;
  motoristaTelefone?: string | null;
}

/** Nenhum campo derivado — ver o aviso no topo do arquivo. */
export interface CargaPatch {
  variedade?: string | null;
  praca?: string | null;
  dataCarregamento?: string | null;
  alvoKg?: number | null;
  kgBruto?: number | null;
  placa?: string | null;
  motoristaNome?: string | null;
  motoristaTelefone?: string | null;
  observacao?: string | null;
  destinoContatoId?: string | null;
  precoVendaKg?: number | null;
  mostrarDespesas?: boolean;
}

/**
 * O produtor de um registro: um contato que já existe, OU um novo.
 *
 * `criarNovo` não é conveniência: sem ele, digitar "Ze Carlos" para o "Zé
 * Carlos" que já está na carteira criaria o segundo cadastro em silêncio, e a
 * pergunta "quanto devo ao Zé" passaria a ter duas respostas. A rota devolve 409
 * `PRODUTOR_PARECIDO` com a sugestão, e a tela pergunta.
 */
export interface ProdutorDoItem {
  contatoId?: string;
  nome?: string;
  telefone?: string | null;
  cidade?: string | null;
  criarNovo?: boolean;
}

export interface NovoItemRegistrado {
  produtor: ProdutorDoItem;
  itens: ItemRegistrado[];
  vencimento?: string | null;
  /**
   * A data do DOCUMENTO — o dia em que carregou na fazenda (073).
   *
   * Ausente dentro de uma carga: cai no `data_carregamento` dela. No romaneio
   * avulso (`registrarRomaneio`) é OBRIGATÓRIA, porque não há carga de onde
   * herdar — e é ela que define o mês, a semana e o vencimento padrão.
   */
  dataDocumento?: string;
  /** O nº do talão de papel do corretor ("2214"). Opcional — nem todo mundo numera. */
  numeroBloco?: string | null;
}

/** Uma linha do painel "Chamar produtores" — um `wa.me` por contato. */
export interface LinkChamadaDTO {
  contatoId: string;
  nome: string;
  url: string;
}

export interface ChamadaDTO {
  texto: string;
  links: LinkChamadaDTO[];
  /** kg que faltam — a tela avisa quando não há alvo. */
  faltam: number | null;
}

// ─── Os DOIS links públicos (C5b) ────────────────────────────────────────────
//
// ⚠️ ESTE TRECHO É A FONTE DA VERDADE DO QUE SAI DA ORGANIZAÇÃO. Do outro lado
// dos dois links tem alguém que não é o comprador — o distribuidor em `/c/`, o
// produtor sem conta em `/p/` — e "vazou junto" nunca pode ser um acidente de
// spread. As listas `CHAVES_*` existem para o teste de contrato: acrescentar
// campo sem passar por aqui quebra o teste, que é o objetivo.
//
// O QUE NUNCA ATRAVESSA: custo, margem, ids internos, `orgId`, e — no `/c/` — o
// PREÇO QUE O COMPRADOR PAGOU a cada produtor. O distribuidor está do outro lado
// da negociação; o preço pago é a margem do corretor escrita por extenso.

/**
 * Uma linha do consolidado — com preço de VENDA ao destino.
 *
 * ⚠️ FORA DO DOCUMENTO desde os Cargas v2 (D4): a carga, nesta fase, é o
 * controle do corretor, sem dados de venda. O tipo fica porque as colunas ficam
 * no banco e `derivados.ts` continua sabendo calcular a margem — o que mudou é
 * que nada disso atravessa para o cliente.
 */
export interface LinhaConsolidadaDTO {
  variedade: string;
  qualidade: string | null;
  kg: number;
  caixas: number | null;
  precoKg: number | null;
  valor: number | null;
}

/** Uma coluna da tabela do documento — uma classificação presente na carga. */
export interface ColunaDoDocumentoDTO {
  chave: string;
  rotulo: string;
}

/**
 * Uma linha da tabela do documento: um produtor e o que ele entregou.
 *
 * ⚠️ `nome` já vem RESOLVIDO pela decisão D5 — quando o corretor desligou "nome
 * dos produtores", o servidor manda "Produtor 1", "Produtor 2"… e `fazenda`
 * `null`. Resolver isso na tela do cliente deixaria o nome verdadeiro viajando
 * no JSON, e o link é público.
 */
export interface ProdutorPublicoDTO {
  nome: string;
  fazenda: string | null;
  /** kg por chave de classificação — a chave é a de `colunas`. */
  porClassificacao: Record<string, number>;
  totalKg: number;
}

export interface CargaPublicaDTO {
  identificador: string;
  /** A data em que o documento foi emitido (o fechamento da carga). */
  emitidoEm: string | null;
  dataCarregamento: string | null;
  comprador: { nome: string; cidade: string | null; telefone: string | null };
  /** O CLIENTE — "destino" no banco, "Cliente" em toda a superfície (D3). */
  cliente: { nome: string; local: string | null } | null;
  placa: string | null;
  motoristaNome: string | null;
  colunas: ColunaDoDocumentoDTO[];
  produtores: ProdutorPublicoDTO[];
  totaisPorClassificacao: Record<string, number>;
  kgLiquido: number;
  /** `null` quando não há bruto OU quando o corretor desligou (D5). */
  kgBruto: number | null;
  nRomaneios: number;
  status: string;
  /** PR D. `A_RECEBER` até os recebimentos existirem. */
  statusRecebimento: string;
}

/**
 * ⚠️ SAÍRAM NESTA FASE (D4): `consolidado`, `despesas`, `totalValor`,
 * `vencimento` e `pagamento`. Sem dados de venda, um "Total: R$ —" ou uma chave
 * PIX no link do cliente prometeriam uma cobrança que não existe. As colunas
 * continuam no banco; é este teste de contrato que garante que elas não voltem
 * ao DTO por acidente de spread.
 */
export const CHAVES_DTO_CARGA_PUBLICA = [
  "identificador",
  "emitidoEm",
  "dataCarregamento",
  "comprador",
  "cliente",
  "placa",
  "motoristaNome",
  "colunas",
  "produtores",
  "totaisPorClassificacao",
  "kgLiquido",
  "kgBruto",
  "nRomaneios",
  "status",
  "statusRecebimento",
] as const;

/** O que o PRODUTOR SEM CONTA vê em `/p/:token` — só o que é dele. */
export interface ItemPublicoDTO {
  comprador: { nome: string; cidade: string | null; telefone: string | null };
  dataCarregamento: string | null;
  produtor: { nome: string };
  itens: ItemRegistrado[];
  totalKg: number;
  totalValor: number;
  vencimento: string | null;
  conferidoEm: string | null;
  /** PR C. `A_PAGAR` até os lançamentos existirem. */
  statusPagamento: string;
}

export const CHAVES_DTO_ITEM_PUBLICO = [
  "comprador",
  "dataCarregamento",
  "produtor",
  "itens",
  "totalKg",
  "totalValor",
  "vencimento",
  "conferidoEm",
  "statusPagamento",
] as const;

/**
 * Chaves que NÃO podem aparecer em DTO público nenhum, em nenhum nível.
 *
 * É a rede embaixo das listas acima: elas dizem o que SAI, esta diz o que jamais
 * pode sair. Um objeto aninhado novo que carregue `custo` passa pela primeira e
 * é pego por esta.
 */
export const CHAVES_PROIBIDAS_DTO_COMPRADOR = [
  "custo", "custoDesembarcadoKg", "margem", "margemPct", "margemKg",
  "orgId", "org_id", "orgFormadoraId", "orgCompradoraId", "orgProdutorId",
  "cargaId", "compartilhamentoId", "produtorContatoId", "destinoContatoId",
  "criadoPorUserId", "token", "resumo", "precoVendaKg",
  // Cargas v2 (D4): o consolidado com preço de venda, as despesas do caminhão e
  // o valor da venda saíram do DTO da carga e não voltam por spread distraído.
  //
  // ⚠️ `totalValor`, `vencimento` e `pagamento` NÃO entram nesta lista, e a
  // diferença é de público: o PRODUTOR vê o próprio total, o próprio prazo e
  // onde o dinheiro cai (é o `/p/` dele, e são os números que ele confere). Quem
  // não vê nada disso é o CLIENTE, e o teste que garante isso é o das chaves de
  // `CHAVES_DTO_CARGA_PUBLICA` — não este, que vale para os dois links.
  "consolidado", "despesas", "valorVenda",
] as const;

// ─── Códigos de erro (a tela reage a eles, não ao texto) ─────────────────────

export const ERRO_COMPRADOR = {
  /** O romaneio já está em outra carga (regra 13). O corpo traz `identificador`. */
  ROMANEIO_EM_CARGA: "ROMANEIO_EM_CARGA",
  /** Há contato parecido na carteira. O corpo traz `sugestao`. */
  PRODUTOR_PARECIDO: "PRODUTOR_PARECIDO",
  /** Fechar exige pelo menos um item. */
  CARGA_VAZIA: "CARGA_VAZIA",
  /** A carga não está no estado que a ação pede. */
  CARGA_ESTADO: "CARGA_ESTADO",
  /** Item de romaneio não se edita: o dono do número é o produtor. */
  ITEM_NAO_EDITAVEL: "ITEM_NAO_EDITAVEL",
  /** Item com pagamento lançado não se apaga (PR C). */
  ITEM_COM_PAGAMENTO: "ITEM_COM_PAGAMENTO",
} as const;

// ─── A pagar (C4) ────────────────────────────────────────────────────────────

/** Uma dívida do comprador: um romaneio formalizado ou um registro próprio. */
export interface LinhaAPagarDTO {
  /** ROMANEIO | REGISTRO (dívida com o produtor) | CARGA (dívida do distribuidor). */
  natureza: NaturezaDivida;
  /** O id do ALVO do lançamento: compartilhamento (ROMANEIO) ou item (REGISTRO). */
  alvoId: string;
  /** "042/2026" ou "registro". */
  documento: string;
  produtorNome: string;
  produtorContatoId: string | null;
  /** true = o produtor tem conta, e é no Rizoma que ele vê o romaneio pago. */
  noRizoma: boolean;
  /**
   * O WhatsApp do produtor.
   *
   * ⚠️ SEM USO NA TELA HOJE. Ele abria o botão "Lembrar", que saiu junto com a
   * fila de espera — não há mais o que lembrar. Fica no DTO porque avisar que o
   * pagamento saiu é o próximo ciclo (mensagens), e é este número que ele vai
   * usar; `null` quando não há número cadastrado.
   */
  telefone: string | null;
  cargaId: string | null;
  cargaIdentificador: string | null;
  kg: number;
  /** O que este documento vale — conferido manda sobre emitido. */
  base: number;
  /** base − confirmado, nunca negativo. */
  saldo: number;
  vencimento: string | null;
  status: string;
  /** Dias até vencer (negativo = vencido); `null` sem vencimento. */
  dias: number | null;
  /** O romaneio ainda não foi conferido pelo comprador. */
  semConferir: boolean;
  /** Motivo da contestação em aberto, quando há. */
  contestacao: string | null;
  /**
   * Pagou MAIS do que devia. A dívida está quitada (`status` PAGO) e o que sobra
   * é conversa — ela aparece em "Precisa de você", no Início.
   */
  excedente: boolean;
  /** Quanto passou. 0 quando não há excedente. */
  excedenteValor: number;
}

export interface GrupoAPagarDTO {
  total: number;
  itens: LinhaAPagarDTO[];
}

export interface APagarDTO {
  /**
   * ⚠️ Os KPIs NÃO olham os filtros: são a foto da dívida inteira. O tile
   * "Vencido" não pode encolher porque a busca está em "Zé" — ele responde
   * "quanto eu devo atrasado", e a resposta não depende do que está na lista.
   */
  kpis: {
    emAberto: number;
    emAbertoDocumentos: number;
    emAbertoProdutores: number;
    venceSemana: number;
    venceSemanaDocumentos: number;
    vencido: number;
    vencidoDocumentos: number;
    pagoNoMes: number;
  };
  /** A lista da tela, com filtros, contagens dos chips e subtotais — tudo do servidor. */
  lista: ListaAPagarDTO;
  /** Os quatro baldes são só de DATA — a pergunta é quando o dinheiro sai. */
  grupos: {
    vencidas: GrupoAPagarDTO;
    semana: GrupoAPagarDTO;
    proximas: GrupoAPagarDTO;
    semVencimento: GrupoAPagarDTO;
  };
  /**
   * Quitados com dinheiro a MAIS.
   *
   * ⚠️ Lista separada porque eles são PAGO: saíram dos grupos de vencimento (não
   * têm saldo) e do grupo de espera (não há o que confirmar). Sem esta lista, o
   * excedente não teria por onde chegar ao "Precisa de você" — e voltaria a ser
   * exatamente o silêncio que a regra existe para evitar.
   */
  excedentes: LinhaAPagarDTO[];
  /** Cargas entregues com saldo — o card verde de "pagar a carga inteira". */
  cargasComSaldo: {
    cargaId: string;
    identificador: string;
    entregueEm: string | null;
    nProdutores: number;
    saldo: number;
  }[];
  janelaSemana: { inicio: string; fim: string };
  /** Agregado por produtor — a outra visão da MESMA união. */
  porProdutor: {
    contatoId: string | null;
    nome: string;
    noRizoma: boolean;
    telefone: string | null;
    documentos: number;
    saldo: number;
    /** O vencimento mais próximo em aberto. */
    proximoVencimento: string | null;
  }[];
}

// ─── A pagar v2: filtros, agrupamento e lote ─────────────────────────────────

/**
 * Os chips da tela. Cada documento cai em EXATAMENTE um balde de vencimento
 * (VENCIDO, SEMANA, PROXIMOS, SEM_VENCIMENTO) ou em PAGO; ABERTO é a soma dos
 * quatro baldes de vencimento.
 *
 * ⚠️ Não existe "aguardando o produtor": quem registra quita (#246), e um chip
 * que só pode estar vazio seria a promessa de que alguém ainda vai responder.
 */
export const SITUACOES_PAGAR = ["ABERTO", "VENCIDO", "SEMANA", "PROXIMOS", "SEM_VENCIMENTO", "PAGO"] as const;
export type SituacaoPagar = typeof SITUACOES_PAGAR[number];

/** RIZOMA = romaneio do Rizoma; REGISTRO_PROPRIO = o que o corretor anotou. */
export const ORIGENS_PAGAR = ["todas", "RIZOMA", "REGISTRO_PROPRIO"] as const;
export type OrigemPagar = typeof ORIGENS_PAGAR[number];

export const AGRUPAMENTOS_PAGAR = ["vencimento", "produtor", "nenhum"] as const;
export type AgrupamentoPagar = typeof AGRUPAMENTOS_PAGAR[number];

/** Quantos documentos cabem num lote. Mais que isso é pedido errado, não pagamento. */
export const LOTE_MAXIMO = 50;

export interface FiltroAPagar {
  /** Padrão ABERTO. */
  situacao?: SituacaoPagar;
  produtorContatoId?: string;
  origem?: OrigemPagar;
  q?: string;
  /** A janela dos PAGOS, pela data do DINHEIRO. Padrão: o mês de hoje. */
  pagosDe?: string;
  pagosAte?: string;
  /** Padrão "vencimento". */
  agrupar?: AgrupamentoPagar;
}

/** Uma linha da lista: a linha de "A pagar" com o que a tabela nova precisa a mais. */
export interface LinhaListaPagarDTO extends LinhaAPagarDTO {
  /** A data do documento (venda ou carregamento). */
  data: string | null;
  situacao: SituacaoPagar;
  /** Quando o dinheiro saiu (o lançamento mais recente); `null` se não é PAGO. */
  pagoEm: string | null;
  forma: string | null;
  /** O que já foi pago neste documento. */
  valorPago: number;
  /** Entra no lote: tem saldo e é dívida com PRODUTOR (a do distribuidor tem rota própria). */
  selecionavel: boolean;
}

export interface GrupoListaPagarDTO {
  chave: string;
  /** `null` = sem agrupamento: a lista corrida, sem cabeçalho. */
  titulo: string | null;
  tom: "perigo" | "alerta" | null;
  n: number;
  /** Saldo (em aberto) ou valor pago (na situação PAGO) — somado no servidor. */
  subtotal: number;
  linhas: LinhaListaPagarDTO[];
}

export interface ListaAPagarDTO {
  situacao: SituacaoPagar;
  agrupar: AgrupamentoPagar;
  /** Com produtor, origem e busca aplicados; a situação não — é ela que o chip escolhe. */
  contagens: Record<SituacaoPagar, number>;
  grupos: GrupoListaPagarDTO[];
  totais: { documentos: number; kg: number; valor: number };
  /** A janela usada para os PAGOS. */
  pagos: { de: string; ate: string };
}

/** Um documento do lote, com o valor que vai ser lançado nele. */
export interface ItemLotePagamento {
  natureza: "ROMANEIO" | "REGISTRO";
  alvoId: string;
  valor: number;
}

export interface ResultadoLoteDTO {
  loteId: string;
  lancados: number;
  total: number;
}

// ─── A receber e o Início (C8b) ──────────────────────────────────────────────

/** Uma carga entregue esperando o dinheiro do destino. */
export interface LinhaAReceberDTO {
  cargaId: string;
  identificador: string;
  destinoNome: string | null;
  destinoCidade: string | null;
  /** Para cobrar pelo WhatsApp. `null` some o botão — `wa.me` sem número não abre. */
  destinoTelefone: string | null;
  /** O destino tem conta no Rizoma — então ele INFORMA e o corretor confirma. */
  destinoNoRizoma: boolean;
  /** O destino já abriu o link `/c/` (o token existe). */
  linkAberto: boolean;
  kg: number;
  /** `valor_venda` — o snapshot congelado ao fechar. */
  base: number;
  saldo: number;
  entregueEm: string | null;
  vencimento: string | null;
  status: string;
  dias: number | null;
  contestacao: string | null;
  /** Recebeu mais do que a carga valia — ver `excedente` em "A pagar". */
  excedente: boolean;
  /** Quanto passou. 0 quando não há excedente. */
  excedenteValor: number;
  /** O lançamento informado pelo DESTINO esperando o corretor confirmar. */
  recebimentoPendente: {
    id: string;
    valor: number;
    data: string;
  } | null;
}

export interface GrupoAReceberDTO {
  total: number;
  itens: LinhaAReceberDTO[];
}

export interface AReceberDTO {
  kpis: {
    emAberto: number;
    emAbertoCargas: number;
    venceSemana: number;
    vencido: number;
    recebidoNoMes: number;
  };
  grupos: {
    vencidas: GrupoAReceberDTO;
    semana: GrupoAReceberDTO;
    proximas: GrupoAReceberDTO;
    semVencimento: GrupoAReceberDTO;
  };
  /** Informados pelo destino, esperando o corretor confirmar. */
  aguardando: GrupoAReceberDTO;
  /** Quitadas com dinheiro a MAIS — ver `excedentes` em `APagarDTO`. */
  excedentes: LinhaAReceberDTO[];
  janelaSemana: { inicio: string; fim: string };
}

/** Uma semana do fluxo de caixa. */
export interface SemanaFluxoDTO {
  /** Segunda-feira, ISO. */
  inicio: string;
  /** Domingo, ISO. */
  fim: string;
  /** É a semana que contém hoje. */
  atual: boolean;
  aPagar: number;
  aReceber: number;
  /** aReceber − aPagar. ASSINADO: negativo é dinheiro faltando. */
  saldo: number;
}

export interface FluxoDeCaixaDTO {
  semanas: SemanaFluxoDTO[];
  /**
   * O que NÃO entra na conta por falta de data.
   *
   * ⚠️ Não é detalhe de rodapé: um fluxo de caixa que ignora R$ 87 mil sem
   * vencimento em silêncio mente sobre o mês. O rodapé diz quanto ficou de fora
   * e leva para onde se resolve.
   */
  semVencimento: { qtd: number; valor: number };
}

/** Uma linha de "Precisa de você" — o que só a pessoa pode resolver. */
export interface PendenciaCompradorDTO {
  tipo: string;
  texto: string;
  sub: string | null;
  acao: { rotulo: string; href: string };
}

/** Um card de "Cargas em andamento" no Início. */
export interface CargaEmAndamentoDTO {
  cargaId: string;
  identificador: string;
  status: string;
  dataCarregamento: string | null;
  alvoKg: number | null;
  kgLiquido: number;
  faltam: number | null;
  /** Só quando entregue e com saldo. */
  aReceber: number | null;
  vencimentoVenda: string | null;
}

export interface InicioCompradorDTO {
  /** CORRETOR | DISTRIBUIDOR — decide o 2º KPI e se "A receber" existe. */
  perfil: string | null;
  kpis: {
    aPagar: number;
    aPagarProdutores: number;
    /** `null` para DISTRIBUIDOR — ele não revende a carga. */
    aReceber: number | null;
    aReceberDestinos: number | null;
    /** aReceber − aPagar da semana corrente, ASSINADO. */
    saldoSemana: number;
    entreguesNoMes: number;
    kgNoMes: number;
    /** `null` para DISTRIBUIDOR. */
    margemNoMes: number | null;
    margemPctNoMes: number | null;
    /** `null` para CORRETOR — é a leitura do distribuidor. */
    custoKgNoMes: number | null;
  };
  fluxoDeCaixa: FluxoDeCaixaDTO;
  precisaDeVoce: PendenciaCompradorDTO[];
  cargasEmAndamento: CargaEmAndamentoDTO[];
}
